import React, { useState, useEffect, useCallback, useMemo } from 'react';

const Footer: React.FC = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [systemStatus, setSystemStatus] = useState({
    uptime: '99.98%',
    performance: 'Optimal',
    security: 'Secured'
  });

  // Optimized time formatter with useMemo
  const timeFormatter = useMemo(() => new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Muscat',
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }), []);

  // Optimized time getter with useCallback
  const getCurrentTime = useCallback(() => {
    return timeFormatter.format(currentTime);
  }, [currentTime, timeFormatter]);

  useEffect(() => {
    // Optimized time updates - using separate intervals for better performance
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timeInterval);
  }, []);

  useEffect(() => {
    // Separate interval for system status updates (less frequent)
    const statusInterval = setInterval(() => {
      setSystemStatus(prev => ({
        ...prev,
        uptime: (99.98 + Math.random() * 0.01).toFixed(2) + '%'
      }));
    }, 5000);

    return () => clearInterval(statusInterval);
  }, []);

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-grid">
          <div className="terminal-window contact-terminal">
            <div className="terminal-header">
              <div className="terminal-dots">
                <div className="terminal-dot red pulse"></div>
                <div className="terminal-dot yellow pulse"></div>
                <div className="terminal-dot green pulse"></div>
              </div>
              <span className="terminal-window-title">PaidNerd@contact:~/info</span>
            </div>
            <div className="terminal-content">
              <div className="terminal-line">
                <span className="terminal-prompt">PaidNerd@contact:~$</span>{' '}
                <span className="terminal-command">./contact-info.sh --details</span>
              </div>
              <div className="terminal-output">                <div className="contact-details">
                  <div className="contact-item">
                    <span className="contact-icon">📧</span>
                    <span className="contact-label">Email:</span>
                    <a 
                      href="mailto:code@paidnerd.com" 
                      className="contact-value highlight-green contact-link"
                    >
                      code@paidnerd.com
                    </a>
                  </div>
                  <div className="contact-item">
                    <span className="contact-icon">📱</span>
                    <span className="contact-label">WhatsApp:</span>
                    <a 
                      href="https://wa.me/96891212037" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="contact-value highlight-yellow contact-link"
                    >
                      +96891212037
                    </a>
                  </div>
                  <div className="contact-item">
                    <span className="contact-icon">🏢</span>
                    <span className="contact-label">Location:</span>
                    <span className="contact-value highlight-purple">Muscat, Sultanate of Oman</span>
                  </div>
                  <div className="contact-item">
                    <span className="contact-icon">⏰</span>
                    <span className="contact-label">Local Time:</span>
                    <span className="contact-value highlight-red">{getCurrentTime()} GST</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="terminal-window services-terminal">
            <div className="terminal-header">
              <div className="terminal-dots">
                <div className="terminal-dot red pulse"></div>
                <div className="terminal-dot yellow pulse"></div>
                <div className="terminal-dot green pulse"></div>
              </div>
              <span className="terminal-window-title">Services & Technologies</span>
            </div>
            <div className="terminal-content">
              <div className="terminal-line">
                <span className="terminal-prompt">$</span>{' '}
                <span className="terminal-command">tree services/ --tech-stack</span>
              </div>
              <div className="terminal-output">
                <div className="services-tree">                  <div className="tree-section">
                    <div className="tree-header">🚀 Web Development</div>
                    <div className="tree-items">
                      <div className="tree-item">├── Custom UI/UX Design</div>
                      <div className="tree-item">├── E-commerce Platforms</div>
                      <div className="tree-item">├── Web Application Development</div>
                      <div className="tree-item">└── Performance Optimization</div>
                    </div>
                  </div>
                  <div className="tree-section">
                    <div className="tree-header">⚡ Tech Stack</div>
                    <div className="tree-items">
                      <div className="tree-item">├── React/Next.js & Node.js</div>
                      <div className="tree-item">├── Python/Django & Angular</div>
                      <div className="tree-item">├── Docker & CI/CD Pipelines</div>
                      <div className="tree-item">└── Cloud Security & SSL</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="terminal-window status-terminal">
            <div className="terminal-header">
              <div className="terminal-dots">
                <div className="terminal-dot red pulse"></div>
                <div className="terminal-dot yellow pulse"></div>
                <div className="terminal-dot green pulse"></div>
              </div>
              <span className="terminal-window-title">System Status</span>
            </div>
            <div className="terminal-content">
              <div className="terminal-line">
                <span className="terminal-prompt">$</span>{' '}
                <span className="terminal-command">systemctl status PaidNerd-services</span>
              </div>
              <div className="terminal-output">                <div className="status-display">
                  <div className="status-item">
                    <span className="status-icon">🟢</span>
                    <span className="status-label">Uptime:</span>
                    <span className="status-value highlight-green">{systemStatus.uptime}</span>
                  </div>
                  <div className="status-item">
                    <span className="status-icon">⚡</span>
                    <span className="status-label">Performance:</span>
                    <span className="status-value highlight-cyan">{systemStatus.performance}</span>
                  </div>
                  <div className="status-item">
                    <span className="status-icon">🛡️</span>
                    <span className="status-label">Security:</span>
                    <span className="status-value highlight-yellow">{systemStatus.security}</span>
                  </div>
                  <div className="status-item">
                    <span className="status-icon">🚀</span>
                    <span className="status-label">Projects:</span>
                    <span className="status-value highlight-purple">150+ Completed</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <div className="terminal-window copyright-terminal">
            <div className="terminal-header">
              <div className="terminal-dots">
                <div className="terminal-dot red"></div>
                <div className="terminal-dot yellow"></div>
                <div className="terminal-dot green"></div>
              </div>
              <span className="terminal-window-title">Legal & Copyright</span>
            </div>
            <div className="terminal-content">
              <div className="terminal-line">
                <span className="terminal-prompt">$</span>{' '}
                <span className="terminal-command">cat LICENSE.txt | grep -i copyright</span>
              </div>
              <div className="terminal-output">
                <div className="copyright-info">
                  <div className="copyright-item">
                    <span className="highlight-cyan">© 2024 PaidNerd.</span>{' '}
                    <span>All rights reserved.</span>
                  </div>
                  <div className="copyright-item">                    <span>Web Development Specialists</span>{' '}
                    <span className="highlight-green">| Muscat, Sultanate of Oman</span>
                  </div>
                  <div className="copyright-item">
                    <span className="highlight-yellow">Crafted with ❤️ using Terminal-Inspired Design</span>
                  </div>
                </div>
                
                <div className="footer-signature">
                  <div className="terminal-line">                    <span className="terminal-prompt">PaidNerd@webdev:~$</span>{' '}
                    <span className="typing-animation">echo "Thank you for choosing PaidNerd! 🚀"</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>        <div className="footer-matrix">
          <div className="matrix-overlay"></div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
